import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PhoneNumber } from './types';
import { apiService } from '../../services/api';

interface PhoneNumberFormProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingPhone?: PhoneNumber | null;
}

const PHONE_TYPES = [
  { value: 'business', label: 'Business' },
  { value: 'support', label: 'Support' },
  { value: 'sales', label: 'Sales' },
  { value: 'fax', label: 'Fax' },
  { value: 'google_voice', label: 'Google Voice' },
];

const PhoneNumberForm: React.FC<PhoneNumberFormProps> = ({
  open,
  onClose,
  onSaved,
  editingPhone,
}) => {
  const [label, setLabel] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [type, setType] = useState('business');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!editingPhone;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);

    if (editingPhone) {
      setLabel(editingPhone.label);
      setPhoneNumber(editingPhone.phone_number);
      setType(editingPhone.type || 'business');
      setNotes(editingPhone.notes || '');
    } else {
      setLabel('');
      setPhoneNumber('');
      setType('business');
      setNotes('');
    }
  }, [open, editingPhone]);

  const handleSave = async () => {
    if (!label.trim() || !phoneNumber.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        await apiService.updatePhoneNumber(editingPhone!.id, { label, phoneNumber, type, notes: notes || undefined });
      } else {
        await apiService.createPhoneNumber({ label, phoneNumber, type, notes: notes || undefined });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEditing ? 'Edit Phone Number' : 'Add Phone Number'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Label"
          placeholder="e.g. Main Office, Support Line"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <TextField
          fullWidth
          label="Phone Number"
          placeholder="(555) 123-4567"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          select
          label="Type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{ mb: 2 }}
        >
          {PHONE_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Notes (optional)"
          placeholder="Hours, extensions, etc."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !label.trim() || !phoneNumber.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
          sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
        >
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Phone Number'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneNumberForm;
